/**
 * Design System Tokens
 * 
 * Single source of truth for all design decisions.
 * Update values here to propagate changes across the entire app.
 * 
 * Usage:
 * - Import in components: import { colors, borderRadius } from '../design-system/tokens'
 * - Use in Tailwind config: require('./src/design-system/tokens')
 * - Use in inline styles: style={{ color: colors.primary.DEFAULT }}
 */

// Colors
export const colors = {
  primary: {
    DEFAULT: '#B8AC7F',
    light: '#C3B890',
    dark: '#A5996A',
    // RGBA variants for overlays
    rgba: {
      10: 'rgba(184, 172, 127, 0.1)',
      20: 'rgba(184, 172, 127, 0.2)',
      30: 'rgba(184, 172, 127, 0.3)',
      50: 'rgba(184, 172, 127, 0.5)',
    }
  },
  accent: {
    DEFAULT: '#B8AC7F',
    dark: '#A5996A',
  },
  text: {
    primary: '#222222',
    secondary: '#717171',
    tertiary: '#B0B0B0',
  },
  surface: {
    DEFAULT: '#FFFFFF',
    secondary: '#F7F7F7',
    hover: '#F8F9FA',
  },
  border: {
    DEFAULT: '#DDDDDD',
    light: '#E8E8E8',
    dark: '#CCCCCC',
  },
  backdrop: {
    light: 'rgba(0, 0, 0, 0.5)',
    medium: 'rgba(0, 0, 0, 0.6)',
    dark: 'rgba(0, 0, 0, 0.8)',
  }
};

// Spacing (matches Tailwind scale)
export const spacing = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  '2xl': '3rem',   // 48px
};

// Border Radius
export const borderRadius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  full: '9999px',
  card: '16px',    // Standard card radius
  pill: '9999px',  // Badge/pill radius
};

// Shadows
export const shadows = {
  card: '0 1px 2px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)',
  'card-hover': '0 2px 4px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
  button: '0 1px 2px rgba(0, 0, 0, 0.08)',
  'button-hover': '0 2px 4px rgba(0, 0, 0, 0.12)',
  modal: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  'glass-light': '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
};

// Backdrop Blur
export const backdropBlur = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
};

// Typography
export const typography = {
  fontFamily: {
    sans: ['DM Sans', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'sans-serif'],
    serif: ['DM Sans', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'sans-serif'],
  },
  fontSize: {
    xs: ['12px', { lineHeight: '1.5', letterSpacing: '-0.01em' }],
    sm: ['14px', { lineHeight: '1.5', letterSpacing: '-0.01em' }],
    base: ['14px', { lineHeight: '1.5', letterSpacing: '-0.01em' }],
    lg: ['16px', { lineHeight: '1.5', letterSpacing: '-0.015em' }],
    xl: ['18px', { lineHeight: '1.4', letterSpacing: '-0.02em' }],
    '2xl': ['22px', { lineHeight: '1.4', letterSpacing: '-0.025em' }],
    '3xl': ['26px', { lineHeight: '1.3', letterSpacing: '-0.03em' }],
    '4xl': ['30px', { lineHeight: '1.2', letterSpacing: '-0.035em' }],
  },
};

// Transitions
export const transitions = {
  fast: '150ms',
  base: '200ms',
  slow: '300ms',
  easing: {
    default: 'ease',
    in: 'ease-in',
    out: 'ease-out',
    inOut: 'ease-in-out',
  }
};

// Glass Morphism Presets
export const glass = {
  light: {
    background: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
  },
  medium: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
  },
  dark: {
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
};

// Export all tokens as a single object for easy access
export const tokens = {
  colors,
  spacing,
  borderRadius,
  shadows,
  backdropBlur,
  typography,
  transitions,
  glass,
};
